import React, { useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { ArrowLeftCircle, Upload } from "lucide-react";
import { db, storage } from "./firebase/firebaseConfig";

const Container = styled.div`
  box-sizing: border-box;
  min-height: 100vh;
  background:
    radial-gradient(circle at top left, rgba(0, 212, 255, 0.14), transparent 28%),
    radial-gradient(circle at bottom right, rgba(255, 122, 24, 0.1), transparent 26%),
    #0a0d12;
  color: #eef4ff;
  padding: 38px;

  * {
    box-sizing: border-box;
  }

  @media (max-width: 640px) {
    padding: 22px 14px;
  }
`;

const Header = styled.div`
  max-width: 920px;
  margin: 0 auto 22px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-width: 0;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Title = styled.h2`
  margin: 0;
  color: #f8fbff;
  font-size: 1.85rem;
  font-weight: 800;
`;

const Subtitle = styled.p`
  margin: 8px 0 0;
  color: #a8b3c7;
  font-weight: 500;
`;

const Form = styled.form`
  width: 100%;
  max-width: 920px;
  margin: 0 auto;
  display: grid;
  gap: 16px;
  background: rgba(12, 18, 28, 0.78);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 22px;
  box-shadow: 0 24px 45px rgba(0, 0, 0, 0.18);
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 14px;
  min-width: 0;
`;

const FieldGroup = styled.label`
  display: grid;
  gap: 8px;
  min-width: 0;
  color: #a8b3c7;
  font-weight: 700;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  min-width: 0;
  padding: 12px 13px;
  border-radius: 7px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: #101722;
  color: #eef4ff;
  font-size: 0.95rem;

  &:focus {
    border-color: rgba(0, 212, 255, 0.65);
    outline: none;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  min-height: 140px;
  resize: vertical;
  padding: 12px 13px;
  border-radius: 7px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: #101722;
  color: #eef4ff;
  font-size: 0.95rem;
  line-height: 1.5;

  &:focus {
    border-color: rgba(0, 212, 255, 0.65);
    outline: none;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  min-width: 0;

  @media (max-width: 520px) {
    > button {
      width: 100%;
      justify-content: center;
    }
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  border-radius: 8px;
  padding: 12px 18px;
  font-weight: 800;
  cursor: pointer;
  color: #071018;
  background: linear-gradient(135deg, #00d4ff, #7dd3fc);
  opacity: ${({ disabled }) => (disabled ? 0.65 : 1)};

  &:hover {
    transform: ${({ disabled }) => (disabled ? "none" : "translateY(-2px)")};
  }
`;

const BackButton = styled(Button)`
  color: #d7dee9;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const FileHint = styled.span`
  color: #7f8da3;
  font-size: 0.82rem;
  font-weight: 600;
`;

const AddDemo = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "",
    description: "",
    priceRange: "",
  });
  const [images, setImages] = useState([]);
  const [video, setVideo] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const uploadFile = async (file, path) => {
    const fileRef = ref(storage, `${path}/${Date.now()}-${file.name}`);
    await uploadBytes(fileRef, file);
    return getDownloadURL(fileRef);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.title.trim() || !form.description.trim()) {
      alert("Title and description are required");
      return;
    }

    try {
      setUploading(true);
      const imageURLs = await Promise.all(
        images.map((image) => uploadFile(image, "demos/images"))
      );
      const videoURL = video ? await uploadFile(video, "demos/videos") : "";

      await addDoc(collection(db, "demos"), {
        title: form.title.trim(),
        description: form.description.trim(),
        priceRange: form.priceRange.trim(),
        imageURLs,
        videoURL,
        createdAt: serverTimestamp(),
      });

      alert("Demo uploaded successfully");
      navigate("/demos");
    } catch (err) {
      console.error(err);
      alert("Error uploading demo");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Container>
      <Header>
        <div>
          <Title>Add New Demo</Title>
          <Subtitle>Upload demo images, video, description and price range.</Subtitle>
        </div>
        <BackButton type="button" onClick={() => navigate("/demos")}>
          <ArrowLeftCircle size={18} /> Back
        </BackButton>
      </Header>

      <Form onSubmit={handleSubmit}>
        <FormGrid>
          <FieldGroup>
            Demo Title
            <Input
              type="text"
              name="title"
              placeholder="Ecommerce app demo"
              value={form.title}
              onChange={handleChange}
            />
          </FieldGroup>
          <FieldGroup>
            Price Range
            <Input
              type="text"
              name="priceRange"
              placeholder="Rs 25,000 - Rs 75,000"
              value={form.priceRange}
              onChange={handleChange}
            />
          </FieldGroup>
        </FormGrid>

        <FieldGroup>
          Description
          <Textarea
            name="description"
            placeholder="Write demo details, features, platform, timeline..."
            value={form.description}
            onChange={handleChange}
          />
        </FieldGroup>

        <FormGrid>
          <FieldGroup>
            Demo Images
            <Input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => setImages(Array.from(e.target.files || []))}
            />
            <FileHint>{images.length} image(s) selected</FileHint>
          </FieldGroup>
          <FieldGroup>
            Demo Video
            <Input
              type="file"
              accept="video/*"
              onChange={(e) => setVideo(e.target.files?.[0] || null)}
            />
            <FileHint>{video ? video.name : "No video selected"}</FileHint>
          </FieldGroup>
        </FormGrid>

        <ButtonRow>
          <Button type="submit" disabled={uploading}>
            <Upload size={18} /> {uploading ? "Uploading..." : "Upload Demo"}
          </Button>
          <BackButton type="button" onClick={() => navigate("/demos")}>
            Cancel
          </BackButton>
        </ButtonRow>
      </Form>
    </Container>
  );
};

export default AddDemo;
